<template>
  <div class="min-h-screen bg-canvas">
    <!-- Skip to main content (accessibility) -->
    <a
      href="#main-content"
      class="sr-only focus:not-sr-only focus:absolute focus:top-4 focus:left-4
             bg-blue-600 text-white px-4 py-2 rounded-lg z-50 text-sm font-medium"
    >
      {{ $t('nav.skipToContent') }}
    </a>

    <DemoBanner />

    <Navbar v-if="authStore.isAuthenticated" />

    <main
      id="main-content"
      :class="authStore.isAuthenticated ? 'max-w-7xl mx-auto p-6 pb-[calc(6.5rem_+_env(safe-area-inset-bottom))] md:pb-6' : ''"
      tabindex="-1"
    >
      <router-view v-slot="{ Component, route }">
        <keep-alive :include="cachedViews">
          <component :is="Component" :key="route.path" />
        </keep-alive>
      </router-view>
    </main>

    <Toast />
    <ConfirmDialog />
    <PwaUpdatePrompt />
  </div>
</template>

<script setup>
import { watch } from 'vue'
import { useRouter } from 'vue-router'
import { useAuthStore } from '@/stores/auth'
import { useSettingsStore } from '@/stores/settings'
import { useDemoMode } from '@/composables/useDemoMode'
import { initAnalytics } from '@/utils/analytics'
import { setI18nLocale } from '@/i18n'
import Navbar from '@/components/layout/Navbar.vue'
import Toast from '@/components/common/Toast.vue'
import ConfirmDialog from '@/components/common/ConfirmDialog.vue'
import PwaUpdatePrompt from '@/components/common/PwaUpdatePrompt.vue'
import DemoBanner from '@/components/common/DemoBanner.vue'

const authStore = useAuthStore()
const settingsStore = useSettingsStore()
const router = useRouter()

// Detect demo instance once at startup (public endpoint, pre-auth safe).
const { isDemoMode } = useDemoMode()
useDemoMode().initDemoMode()

// Cookieless usage stats — only ever activates when isDemoMode is true.
initAnalytics(router, isDemoMode)

// Cache main views to avoid re-mount flicker on navigation
const cachedViews = ['DashboardView', 'ExpensesView', 'UtilitiesView', 'ProjectsView', 'SettingsView']

if (authStore.isAuthenticated) {
  settingsStore.loadSettings()
  settingsStore.loadHouseholdSettings()
}

watch(() => authStore.isAuthenticated, (isAuth) => {
  if (isAuth) {
    settingsStore.loadSettings()
    settingsStore.loadHouseholdSettings()
  } else {
    settingsStore.$reset()
  }
})

watch(
  () => settingsStore.language,
  (lang) => setI18nLocale(lang),
  { immediate: true }
)
</script>

